import { useRef, useEffect, useState } from "react";
import { IFieldInfo, ISigner } from "../GenericForms/formTypes";
import PdfInput from "./PdfInput";
import PdfLink from "./PdfLink";
import { ISigningDefaults, ISigningElement } from "./PdfSigningSidebar";
import SigningElement from "./SigningElement";
import { NewGuid } from "../../utils/Tools";

interface IPdfViewPageProps {
    id:string;
    viewOnly:boolean;
    scale:number;
    inputValueChanged:Function;
    formValues: any;
    //tabToNext:Function;
    fieldsMetaData:Record<string,IFieldInfo>;    
    missingFields:Record<string,IFieldInfo>;    
    signingRequested:Function;
    isSetupMode:boolean;
    signer?:ISigner;
    fieldSelected?:(x:IFieldInfo)=>void;
    fieldHighlighed?:IFieldInfo;
    //pageAnnotations:any[];
    //tabIndex:number;
    page:any;
    scrollTo:boolean;
    signingDefaults: ISigningDefaults;
    signingElements: ISigningElement[]
    signingDefaultsChanged: (defaults: ISigningDefaults) => void;
    signingElementUpdated: (element: ISigningElement) => void;
    signingElementRemoved: (element: ISigningElement) => void;
    signingElemenetSelected: (element: ISigningElement | null) => void;
    allowSigning:boolean;
    placementMode: {
        isActive: boolean;
        elementType: ISigningElement['type'] | null;
        previewPosition: { x: number; y: number };
    };
    setPlacementMode: React.Dispatch<React.SetStateAction<{
        isActive: boolean;
        elementType: ISigningElement['type'] | null;
        previewPosition: { x: number; y: number };
    }>>;
}


const PdfViewerPage = (props:IPdfViewPageProps) => {
        const canvasRef = useRef<HTMLCanvasElement>();
        const renderTaskRef = useRef<any>(null);
        const [page, setPage] = useState<any>();
        const [pageAnnotations, setAnnotations] = useState<any[]>([]);
        const [tabIndex, setTabIndex] = useState<number>(-1);
        const [selectedElement, setSelectedElement] = useState<ISigningElement | null>(null);
        const [scale, setScale] = useState<number>(props.scale);



    useEffect(()=>{
        if(props.page){// && !page){
            setPage(props.page);
            console.log('Page Set');
        }
    },[props.page]);

    
    useEffect(()=>{
        props.signingElemenetSelected(selectedElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[selectedElement]);

    useEffect(()=>{
        if(props.scrollTo){
            canvasRef?.current.scrollIntoView();
        }
    }, [props.scrollTo]);

    

  // Handle escape key to cancel placement mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.placementMode.isActive) {
        props.setPlacementMode({
          isActive: false,
          elementType: null,
          previewPosition: { x: 0, y: 0 },
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.placementMode.isActive]);

    useEffect(()=>{
        
        if(page){
            // Cancel any previous render operation
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
                renderTaskRef.current = null;
            }

            let viewport = page.getViewport({ scale: props.scale });
          if(viewport.width > 1000){
            let w = viewport.width / props.scale;
            let s = 950 / w;
            viewport = page.getViewport({ scale: s });
            setScale(s);
          }

          const canvas: any = canvasRef.current;
          if (!canvas) {
            return;
          }
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          const renderContext = {
            canvasContext: canvas.getContext("2d"),
            viewport: viewport,
          };
          
          // Store the render task so we can cancel it if needed
          const renderTask = page.render(renderContext);
          renderTaskRef.current = renderTask;
          
          renderTask.promise.then(() => {
            // Clear the render task reference when completed
            if (renderTaskRef.current === renderTask) {
                renderTaskRef.current = null;
            }
          }).catch((error) => {
            // Handle cancellation or other errors
            if (renderTaskRef.current === renderTask) {
                renderTaskRef.current = null;
            }
            // Only log non-cancellation errors
            if (error.name !== 'RenderingCancelledException') {
                console.error('PDF render error:', error);
            }
          });

          page.getAnnotations().then((items) => {
            setAnnotations(items);
            console.info('Annotations Updated:', items);
          });
        }
    },[page, props.formValues, props.id, props.scale]);

    // Cleanup effect to cancel any pending render operation
    useEffect(() => {
        return () => {
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
                renderTaskRef.current = null;
            }
        };
    }, []);

    
      const handleSelectElement = (element: ISigningElement) => {
        setSelectedElement(element);
        props.signingElemenetSelected(element);
      };
    
      const handleMoveElement = (elementId: string, x: number, y: number) => {
        let el = { ...props.signingElements.find((el) => el.id === elementId), x, y };
        props.signingElementUpdated(el);
        setSelectedElement(el);
      };
    
      const handleUpdateElementContent = (elementId: string, content: string) => {
        let el = { ...props.signingElements.find((el) => el.id === elementId), content };
        props.signingElementUpdated(el);
        setSelectedElement(el);
        //setSigningElements((prev) => prev.map((el) => (el.id === elementId ? { ...el, content } : el)));
      };

    const tabToNext = (index) => {
        for (let i = index + 1; i < pageAnnotations.length; i++) {
          let a = pageAnnotations[i];
          //if (a.hasAppearance && a.fieldType === "Tx") {
          if (a.fieldType === "Tx") {
            setTabIndex(i);
            return;
          }
        }
      };

      

  // Handle placement click
  const handlePlacementClick = (e: React.MouseEvent) => {
    if (!props.placementMode.isActive || !props.placementMode.elementType) return;

    e.stopPropagation();

    // Find the PDF page that was clicked
    const clickedElement = e.target as Element;
    const pdfPage = clickedElement.closest('.zpdf-page');
    const pageContainer = clickedElement.closest('.zpdf-page-container');

    if (!pdfPage || !pageContainer) return;

    // Find page number
    const allPageContainers = document.querySelectorAll('.zpdf-page-container');
    const pageIndex = Array.from(allPageContainers).indexOf(pageContainer as Element);
    const pageNumber = pageIndex + 1;

    // Calculate position relative to the PDF page canvas
    const pageRect = pdfPage.getBoundingClientRect();
    const x = e.clientX - pageRect.left;
    const y = e.clientY - pageRect.top;

    // Estimate element size (approximate based on type)
    const estimatedWidth = 100; // Approximate width for signing elements
    const estimatedHeight = 30; // Approximate height for signing elements

    // Constrain position to keep element within page boundaries
    const constrainedX = Math.max(0, Math.min(x, pageRect.width - estimatedWidth));
    const constrainedY = Math.max(0, Math.min(y, pageRect.height - estimatedHeight));

    // Create element content based on type
    let content: string | undefined;
    switch (props.placementMode.elementType) {
      case 'signature':
        content = props.signingDefaults.defaultSignature;
        break;
      case 'date':
        content = new Date(props.signingDefaults.defaultDate).toLocaleDateString();
        break;
      case 'text':
        content = '';
        break;
      default:
        content = undefined;
    }

    // Create the new element
    const newElement: ISigningElement = {
      id: NewGuid(),
      type: props.placementMode.elementType,
      x: constrainedX,
      y: constrainedY,
      pageNumber,
      content,
      textColor: props.signingDefaults.textColor,
    };

    // setSigningElements((prev) => [...prev, newElement]);
    setSelectedElement(newElement);
    props.signingElementUpdated(newElement);

    console.log('Placed new element:', newElement);
    // Exit placement mode
    props.setPlacementMode({
      isActive: false,
      elementType: null,
      previewPosition: { x: 0, y: 0 },
    });
  };
  
    const handlePageClick = (e: React.MouseEvent) => {
      // Handle placement if in placement mode
      if (props.placementMode.isActive) {
        // Only place if clicking on a PDF page or page container, not on signing elements
        const target = e.target as Element;
        const isOnPage = target.closest('.zpdf-page') || target.closest('.zpdf-page-container');
        const isOnSigningElement = target.closest('.signing-element');
  
        if (isOnPage && !isOnSigningElement) {
          handlePlacementClick(e);
        }
        return;
      }
  
      // Deselect element when clicking on empty area (not on signing elements)
      const target = e.target as Element;
      if (!target.closest('.signing-element')) {
        setSelectedElement(null);
      }
    };
    
  // Handle mouse movement for preview element positioning
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!props.placementMode.isActive) return;

    // Find the PDF page under the cursor to calculate exact placement position
    const target = e.target as Element;
    const pdfPage = target.closest('.zpdf-page');

    if (pdfPage) {
      // Calculate where the element would actually be placed
      const pageRect = pdfPage.getBoundingClientRect();
      const rawX = e.clientX - pageRect.left;
      const rawY = e.clientY - pageRect.top;

      // Calculate the scaled position where the element will appear
      const scaledX = pageRect.left + rawX;
      const scaledY = pageRect.top + rawY;

      props.setPlacementMode((prev) => ({
        ...prev,
        previewPosition: { x: scaledX, y: scaledY },
      }));
    } else {
      // Fallback to cursor position when not over a page
      props.setPlacementMode((prev) => ({
        ...prev,
        previewPosition: { x: e.clientX, y: e.clientY },
      }));
    }
  };
 
  
    return (<>
        <div className={`zpdf-page scale-${scale} ${props.placementMode.isActive ? 'placement-mode' : ''}`}  onClick={handlePageClick} onMouseMove={handleMouseMove}>
            <canvas className="zpdf-viewer-canvas" ref={canvasRef} />
            {!props.viewOnly && pageAnnotations.length > 0 &&
                pageAnnotations
                  //.filter((x) => x.hasAppearance && x.fieldType)
                  //.filter((x) => x.fieldType)
                  .map((x, i) => {
                    let active = i === tabIndex;
                    return (
                      x.fieldType ? 
                      <PdfInput
                        index={i}
                        tabToNext={tabToNext}
                        hasFocus={active}
                        position={x.rect}
                        value={x.fieldValue}
                        exportValue={x.exportValue ?? x.buttonValue}
                        type={x.fieldType}
                        name={x.fieldName}
                        id={x.fieldName}
                        scale={scale}
                        valueUpdated={props.inputValueChanged}
                        values={props.formValues}
                        key={`page-input-${i}-${x.fieldName}`}
                        fieldMetaData={props.fieldsMetaData[x.fieldName]}
                        canEdit={true}
                        signer={props.signer}       
                        requestSign={props.signingRequested}                 
                        isSetupMode={props.isSetupMode}
                        missingFields={props.missingFields}
                        fieldSelected={()=>{
                          console.log('Viewer Field Selected', props);
                          console.log(x, props.fieldHighlighed);
                          if(props.fieldSelected) props.fieldSelected(x);
                        }}
                        fieldHighligh={props.fieldHighlighed?.FormKey === x.fieldName}
                      /> :
                      x.subtype === "Link" && x.url ?
                      <PdfLink position={x.rect} url={x.url} key={`page-link-${i}-${x.url}`} scale={scale}/>
                      :<></>
                    );
                  })}
            {props.signingElements && props.signingElements.map((el) => (
              <SigningElement
                key={el.id}
                element={el}
                onSelect={() => handleSelectElement(el)}
                onMove={(x, y) => handleMoveElement(el.id, x, y)}
                onContentChange={(content) => handleUpdateElementContent(el.id, content)}
                scale={scale} 
                isSelected={selectedElement?.id === el.id}              />
            ))}
            

              {/* Preview element that follows cursor */}
              {props.placementMode.isActive && props.placementMode.elementType && (
                <div
                  className={`signing-element-preview signing-element-${props.placementMode.elementType}`}
                  style={{
                    left: props.placementMode.previewPosition.x - 1, // Minimal offset to avoid cursor blocking
                    top: props.placementMode.previewPosition.y - 1, // Minimal offset to avoid cursor blocking
                    pointerEvents: 'none',
                    position: 'fixed',
                    zIndex: 500,
                    transform: 'none', // Ensure no transform affects positioning
                    color: props.signingDefaults.textColor,
                    fontFamily: 'Arial, Helvetica, sans-serif',
                  }}>
                  {props.placementMode.elementType === 'signature' && <span className="signature-text">{props.signingDefaults.defaultSignature || 'Signature'}</span>}
                  {props.placementMode.elementType === 'date' && <span className="date-text">{new Date(props.signingDefaults.defaultDate).toLocaleDateString()}</span>}
                  {props.placementMode.elementType === 'checkmark' && <span className="checkmark">✓</span>}
                  {props.placementMode.elementType === 'x' && <span className="x-mark">✗</span>}
                  {props.placementMode.elementType === 'text' && <span className="free-text">Text</span>}
                </div>
              )}
        </div>
    </>);
}

export default PdfViewerPage;